// Participant history, independent feedback and expiring transcripts.

import { api } from "../api.js";
import { register, go, handleError, $ } from "../ui.js";
import { dateTime, duration, endHeading, mmss, MODE_LABEL, reasonShort } from "../format.js";

const POLL_MS = 10_000;
let pollTimer = null;
let viewing = null; // Identity is a view epoch, including same-session revisits.
let feedbackFor = null; // session the feedback block is set up for

/** Renders the transcript block. */
function renderTranscript(row) {
  const box = $("end-transcript");
  box.replaceChildren();
  const say = (text) => {
    const p = document.createElement("p");
    p.textContent = text;
    box.append(p);
  };
  if (row.transcriptStatus === "ready") {
    for (const line of row.transcript) {
      const p = document.createElement("p");
      const who = document.createElement("b");
      who.textContent = `${line.speaker.toLowerCase()}: `;
      p.append(who, document.createTextNode(line.text));
      box.append(p);
    }
  } else if (row.transcriptStatus === 'expired') say('this transcript expired seven days after the call.');
  else if (row.transcriptStatus === "empty") say("no speech was recorded.");
  else if (row.transcriptStatus === "failed") say("the transcript could not be produced.");
  else say("preparing transcript…");
}

// ---------------------------------------------------------------- feedback

const categories = ["quality", "vibe"];
const thumbs = [...document.querySelectorAll("#feedback [data-category][data-rating]")];
// Queues outlive the screen: leaving must not discard an accepted final tap.
const feedbackBySession = new Map();

function renderCategory(category, state) {
  for (const b of thumbs) {
    if (b.dataset.category === category) {
      b.setAttribute("aria-pressed", String(b.dataset.rating === state.rating));
    }
  }
  $(`feedback-${category}-error`).hidden = !state.error;
  $(`feedback-${category}-retry`).hidden = !state.error;
  $(`feedback-${category}-retry`).disabled = state.running;
}

/** Initialise only once per view; transcript polling never resets local choices. */
function renderFeedback(row) {
  const show = !!row.role && row.status === "ended" && !!row.talkerConnectedAt;
  $("feedback").hidden = !show;
  if (!show || feedbackFor === row.id) return;
  feedbackFor = row.id;
  if (!feedbackBySession.has(row.id)) {
    const states = {};
    for (const category of categories) {
      states[category] = {
        rating: row.feedback?.[category] ?? null,
        version: 0, savedVersion: 0, running: false, error: false, view: null,
      };
    }
    // Legacy rating/text are deliberately not interpreted as either category.
    feedbackBySession.set(row.id, states);
  }
  for (const category of categories) {
    const state = feedbackBySession.get(row.id)[category];
    // Retained queues belong to the current eligible view on same-session reentry.
    state.view = viewing;
    renderCategory(category, state);
  }
}

function feedbackVisible(view, sessionId) {
  return viewing === view && feedbackFor === sessionId && !$("feedback").hidden;
}

/** One in-flight write per session/category; newer taps coalesce behind it. */
async function flushFeedback(sessionId, category, state) {
  if (state.running) return;
  state.running = true;
  while (state.savedVersion < state.version) {
    const { rating, version } = state;
    try {
      await api(`/api/session/${sessionId}/feedback`, {
        method: "POST", body: { category, rating },
      });
      state.savedVersion = version;
    } catch (e) {
      // An older failure cannot cancel a newer accepted selection.
      if (version !== state.version) continue;
      state.error = true;
      state.running = false;
      if (feedbackVisible(state.view, sessionId)) {
        renderCategory(category, state);
        if (e.status === 401) handleError(e);
      }
      return;
    }
    if (version === state.version) {
      state.error = false;
      state.running = false;
      if (feedbackVisible(state.view, sessionId)) renderCategory(category, state);
      return;
    }
  }
  state.running = false;
}

function chooseRating(category, rating) {
  if (!viewing || !feedbackFor || $("feedback").hidden) return;
  const sessionId = feedbackFor;
  const state = feedbackBySession.get(sessionId)[category];
  state.rating = rating;
  state.version += 1;
  state.error = false;
  state.view = viewing;
  renderCategory(category, state);
  void flushFeedback(sessionId, category, state);
}

for (const b of thumbs) {
  b.addEventListener("click", () => chooseRating(b.dataset.category, b.dataset.rating));
}
for (const category of categories) {
  $(`feedback-${category}-retry`).addEventListener("click", () => {
    const state = feedbackBySession.get(feedbackFor)?.[category];
    if (state?.error && !state.running) chooseRating(category, state.rating);
  });
}

// ---------------------------------------------------------------- end screen

async function load(view = viewing) {
  if (!view || viewing !== view) return;
  const { sessionId, fromHistory, controller } = view;
  clearTimeout(pollTimer);
  try {
    const row = await api(`/api/sessions/${sessionId}`, { signal: controller.signal });
    if (viewing !== view || controller.signal.aborted) return;

    $("end-title").textContent = row.status === "ended" ? endHeading(row.endReason, row.role) : "session in progress.";
    $("end-time").textContent = row.endedAt ? dateTime(row.endedAt) : "—";
    $("end-duration").textContent = mmss(duration(row));
    $("end-mode").textContent = MODE_LABEL[row.mode];
    renderFeedback(row);
    renderTranscript(row);

    // Seen the end screen: don't show it again on next app open.
    if (!fromHistory && row.status === "ended" && row.role) {
      api(`/api/session/${sessionId}/ack`, { method: "POST", signal: controller.signal }).catch(() => {});
    }

    // Keep refreshing until the record is final (the end may still be landing).
    const final = row.status === "ended" && !["pending", "none"].includes(row.transcriptStatus);
    if (!final && viewing === view) {
      pollTimer = setTimeout(() => load(view), row.status === "ended" ? POLL_MS : 1500);
    }
  } catch (e) {
    if (viewing !== view || controller.signal.aborted || e.name === "AbortError") return;
    handleError(e);
    if (e.status !== 401 && viewing === view) pollTimer = setTimeout(() => load(view), POLL_MS);
  }
}

$("end-done").addEventListener("click", () => go(viewing?.fromHistory ? "past-sessions" : "role"));
$("end-past").addEventListener("click", () => go("past-sessions"));

// ---------------------------------------------------------------- history

let pastView = null;

function renderPastControls(view) {
  $("past-more").hidden = !view.nextCursor || view.error;
  $("past-more").disabled = view.loading;
  $("past-error").hidden = !view.error;
  $("past-retry").hidden = !view.error;
  $("past-retry").disabled = view.loading;
  $("past-list").setAttribute("aria-busy", String(view.loading));
}

async function loadPast(view = pastView) {
  if (!view || pastView !== view || view.loading) return;
  view.loading = true;
  view.error = false;
  renderPastControls(view);
  const path = `/api/sessions?limit=50${view.nextCursor ? `&cursor=${encodeURIComponent(view.nextCursor)}` : ""}`;
  try {
    const { sessions, nextCursor } = await api(path, { signal: view.controller.signal });
    if (pastView !== view || view.controller.signal.aborted) return;
    const list = $("past-list");
    for (const row of sessions) {
      // The server owns stable ordering; retain it and discard overlapping IDs.
      if (view.ids.has(row.id)) continue;
      view.ids.add(row.id);
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      const title = document.createElement("span");
      title.textContent = dateTime(row.created_at);
      const meta = document.createElement("span");
      meta.className = "meta";
      const d = duration({ talkerConnectedAt: row.talker_connected_at, endedAt: row.ended_at });
      meta.textContent = `${MODE_LABEL[row.mode]} · ${mmss(d)} · ${reasonShort(row.end_reason)}`;
      btn.append(title, meta);
      btn.addEventListener("click", () => {
        if (pastView === view) go("end", { sessionId: row.id, fromHistory: true });
      });
      li.append(btn);
      list.append(li);
    }
    view.nextCursor = nextCursor ?? null;
    if (!view.ids.size && !view.nextCursor) {
      const li = document.createElement("li");
      li.textContent = "no sessions yet.";
      li.className = 'empty-state';
      list.replaceChildren(li);
    }
  } catch (e) {
    if (pastView !== view || view.controller.signal.aborted || e.name === "AbortError") return;
    // Keep all previous rows and the failed cursor so retry resumes the same page.
    view.error = true;
    if (e.status === 401) handleError(e);
  } finally {
    if (pastView === view && !view.controller.signal.aborted) {
      view.loading = false;
      renderPastControls(view);
    }
  }
}

$("past-more").addEventListener("click", () => {
  if (pastView?.nextCursor) void loadPast();
});
$("past-retry").addEventListener("click", () => {
  if (pastView?.error) void loadPast();
});

register({
  end: {
    enter({ sessionId, fromHistory = false }) {
      viewing = { sessionId, fromHistory, controller: new AbortController() };
      feedbackFor = null;
      $("feedback").hidden = true;
      $("end-done").textContent = fromHistory ? "back" : "done";
      $("end-past").hidden = fromHistory;
      $("end-title").textContent = "session ended.";
      $("end-time").textContent = "—";
      $("end-duration").textContent = "—";
      $("end-mode").textContent = "—";
      $("end-transcript").replaceChildren();
      load();
    },
    leave() {
      clearTimeout(pollTimer);
      viewing?.controller.abort();
      viewing = null;
      feedbackFor = null;
    },
  },
  "past-sessions": {
    enter() {
      pastView = { controller: new AbortController(), ids: new Set(), nextCursor: null, loading: false, error: false };
      $("past-list").replaceChildren();
      loadPast();
    },
    leave() {
      pastView?.controller.abort();
      pastView = null;
    },
  },
});
